/**
 * Waiver Wire, streamed.
 *
 * The server can send the waiver recommendations as server-sent events
 * (POST .../waiver/recommendations?stream=1): progress steps, then each player
 * the moment the model has finished writing them, then the final answer. The
 * model's writing time is most of the wait, so this is what stops the page
 * looking frozen. Same answer, same quality, only delivered earlier.
 *
 */
import { streamFetch } from './api'
import { parseSseChunk } from './sseParse'

/**
 * Run a streamed waiver request.
 * @param {string|number} leagueId
 * @param {object} body  the filters
 * @param {{ onStatus?:(e:{status:string, step?:number})=>void, onPlayer?:(player:object, written:number)=>void }} handlers
 * @returns {Promise<object>} the final answer ({ players, note, generatedAt })
 * @throws Error with `.message` suitable to show the user; `.streamUnavailable = true` when the
 *         caller should fall back to the ordinary request (the stream never started).
 */
export const streamWaiver = async (leagueId, body, { onStatus, onPlayer } = {}) => {
  let response
  try {
    response = await streamFetch(`/api/leagues/${leagueId}/waiver/recommendations?stream=1`, { method: 'POST', body: JSON.stringify(body) })
  } catch (err) {
    const e = new Error('Network error')
    e.streamUnavailable = true
    e.cause = err
    throw e
  }

  // Refusals (plan, quota, rate limit, wrong league) come back as ordinary JSON.
  if (!response.ok) {
    let message = 'Something went wrong. Try again.'
    try { message = (await response.json()).error || message } catch { /* keep the default */ }
    throw new Error(message)
  }
  if (!response.body?.getReader) {
    const e = new Error('Streaming not supported')
    e.streamUnavailable = true
    throw e
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let final = null
  let failure = null
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const { events, rest } = parseSseChunk(buffer)
    buffer = rest
    for (const ev of events) {
      if (ev.error) failure = ev.error
      else if (ev.status) onStatus?.(ev)
      else if (ev.player) onPlayer?.(ev.player, ev.written || 0)
      else if (ev.final) final = ev.final
    }
  }
  if (failure) throw new Error(failure)
  if (!final) throw new Error('The connection closed before JAI finished. Try again.')
  return final
}
